import React, { useEffect, useState } from 'react';
import { Box, Button, Text, Spinner, HStack, Table, Input, Badge } from '@chakra-ui/react';
import { NativeSelect } from '@chakra-ui/react';
import SimpleModal from './SimpleModal';
import { api, ZatcaXmlRecordItem } from '../api/api';

interface ZatcaXmlArchiveModalProps {
    isOpen: boolean;
    onClose: () => void;
    machineKey: string;
    systemName: string;
}

function formatDateTime(dateString?: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
}

const PAGE_SIZE = 20;

const ZatcaXmlArchiveModal: React.FC<ZatcaXmlArchiveModalProps> = ({ isOpen, onClose, machineKey, systemName }) => {
    const [items, setItems] = useState<ZatcaXmlRecordItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('tax_order_desc');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const fetchData = async () => {
        if (!machineKey) return;
        setLoading(true);
        setError('');
        try {
            const data = await api.zatcaXml.list(machineKey, {
                page,
                pageSize: PAGE_SIZE,
                sortBy,
                from: fromDate || undefined,
                to: toDate || undefined,
            });
            setItems(data.items);
            setTotalCount(data.totalCount);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to load ZATCA XML records.');
            setItems([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, page, sortBy, fromDate, toDate]);

    // Reset filters/page when modal opens
    useEffect(() => {
        if (isOpen) {
            setPage(1);
            setFromDate('');
            setToDate('');
        }
    }, [isOpen]);

    const handleDownload = (recordType: string, recordId: number, invoiceNumber?: string) => {
        const url = api.zatcaXml.downloadUrl(machineKey, recordType, recordId);
        const token = localStorage.getItem('token');
        fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
        })
            .then(res => {
                if (!res.ok) throw new Error('Download failed');
                return res.blob();
            })
            .then(blob => {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `${invoiceNumber || recordId}.xml`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
            })
            .catch(() => setError('Download failed.'));
    };

    return (
        <SimpleModal isOpen={isOpen} onClose={onClose} title={`ZATCA XML Records — ${systemName}`} size="lg">
            <Box mb={3}>
                <HStack gap={3} align="center" wrap="wrap">
                    <Text fontSize="sm" fontWeight="medium">Sort by:</Text>
                    <NativeSelect.Root size="sm" width="200px">
                        <NativeSelect.Field value={sortBy} onChange={(e) => { setSortBy(e.target.value); setPage(1); }}>
                            <option value="tax_order_desc">Tax Order (Newest)</option>
                            <option value="tax_order_asc">Tax Order (Oldest)</option>
                            <option value="date_desc">Sale Date (Newest)</option>
                            <option value="date_asc">Sale Date (Oldest)</option>
                            <option value="uploaded_desc">Uploaded (Newest)</option>
                            <option value="uploaded_asc">Uploaded (Oldest)</option>
                        </NativeSelect.Field>
                    </NativeSelect.Root>
                    <Text fontSize="sm" fontWeight="medium">From:</Text>
                    <Input size="sm" type="date" width="150px" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setPage(1); }} />
                    <Text fontSize="sm" fontWeight="medium">To:</Text>
                    <Input size="sm" type="date" width="150px" value={toDate} onChange={(e) => { setToDate(e.target.value); setPage(1); }} />
                    {(fromDate || toDate) && (
                        <Button size="xs" variant="ghost" onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}>
                            Clear dates
                        </Button>
                    )}
                    <Text fontSize="sm" color="gray.500">{totalCount} record{totalCount !== 1 ? 's' : ''}</Text>
                </HStack>
            </Box>

            {loading ? (
                <Box textAlign="center" py={8}><Spinner size="lg" /></Box>
            ) : error ? (
                <Text color="red.500">{error}</Text>
            ) : items.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={8}>No ZATCA XML records found for this system.</Text>
            ) : (
                <>
                    <Table.Root size="sm" variant="outline">
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>Tax Order</Table.ColumnHeader>
                                <Table.ColumnHeader>Invoice Number</Table.ColumnHeader>
                                <Table.ColumnHeader>Type</Table.ColumnHeader>
                                <Table.ColumnHeader>Sale Date</Table.ColumnHeader>
                                <Table.ColumnHeader>Uploaded At</Table.ColumnHeader>
                                <Table.ColumnHeader>Submitted to ZATCA</Table.ColumnHeader>
                                <Table.ColumnHeader>Download</Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {items.map(item => (
                                <Table.Row key={`${item.recordType}_${item.recordId}`}>
                                    <Table.Cell fontSize="sm">{item.taxOrderNo ?? 'N/A'}</Table.Cell>
                                    <Table.Cell fontSize="sm">{item.invoiceNumber || 'N/A'}</Table.Cell>
                                    <Table.Cell>{item.recordType}</Table.Cell>
                                    <Table.Cell>{formatDateTime(item.saleDate)}</Table.Cell>
                                    <Table.Cell>{formatDateTime(item.uploadedAtUtc)}</Table.Cell>
                                    <Table.Cell>
                                        {item.isVerified ? (
                                            <Badge colorPalette="green">Submitted</Badge>
                                        ) : item.hasIssue ? (
                                            <Badge colorPalette="red" title={item.errorMessage || 'Failing'}>
                                                Failing{item.errorMessage ? `: ${item.errorMessage}` : ''}
                                            </Badge>
                                        ) : (
                                            <Badge colorPalette="yellow">Pending</Badge>
                                        )}
                                    </Table.Cell>
                                    <Table.Cell>
                                        <Button size="xs" colorPalette="blue" variant="outline" onClick={() => handleDownload(item.recordType, item.recordId, item.invoiceNumber)}>
                                            Download
                                        </Button>
                                    </Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table.Root>

                    <HStack justify="center" mt={4} gap={2}>
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                            Previous
                        </Button>
                        <Text fontSize="sm">Page {page} of {totalPages} ({totalCount} total)</Text>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                            Next
                        </Button>
                    </HStack>
                </>
            )}
        </SimpleModal>
    );
};

export default ZatcaXmlArchiveModal;
